// frontend/src/utils/axiosInterceptor.js
import axios from 'axios';
import { SessionManager } from './SessionManager.js';
import { NETWORK_ERROR_EVENT } from '../hooks/useServerReachability';

// Unauthorized 時に呼び出される関数
const redirectOnUnauthorized = () => {
  window.dispatchEvent(new CustomEvent('unauthorized'));
};

// エラー画面に遷移する関数
const navigateToErrorPage = (error) => {
  // ログの送信失敗など軽微なエラーではエラー画面に遷移しない
  if (error.config?.url?.includes('/api/logs') || error.message?.includes('Failed to fetch')) {
    return;
  }

  // エラーログをバックエンドに送信（Fluent Bit経由で収集）
  if (typeof window.logClientEvent === 'function') {
    window.logClientEvent('error', {
      message: error.message || 'API Error',
      status: error.response?.status,
      url: error.config?.url,
      method: error.config?.method,
      responseData: error.response?.data,
    });
  }

  // エラー情報をセッションストレージに保存（エラー画面で表示するため）
  try {
    // レスポンスからエラー情報を抽出
    const errorData = error.response?.data || {};
    const stack = errorData.stack || error.stack || '';
    
    const errorInfo = {
      message: errorData.message || error.message || '不明なエラーが発生しました',
      status: error.response?.status || 500,
      path: window.location.pathname,
      timestamp: new Date().toISOString(),
      stack: stack,
      data: errorData.errors || errorData.data || {}
    };
    
    // 安全にJSON化できることを確認
    sessionStorage.setItem('app_error', JSON.stringify(errorInfo));
  } catch (e) {
    // 最低限のエラー情報だけでも保存を試みる
    try {
      sessionStorage.setItem('app_error', JSON.stringify({
        message: '不明なエラーが発生しました',
        status: 500,
        timestamp: new Date().toISOString()
      }));
    } catch (innerError) {

    }
  }

  // ネットワークエラー（サーバから応答が無い）では /error へ遷移しない。
  // ConnectionLostOverlay が原因（Tailscale 断・院内 LAN 断・サーバ停止）と復旧手順を
  // 出すので、両方出すと画面が二重になる。しかも復旧して［再試行］でオーバーレイが
  // 消えた裏からエラー画面が現れ、/error にいる間はエラーハンドリングが全スキップ
  // （下の early return）されるためユーザーが行き場を失う。
  // ログの送信は上で済ませているので、遷移だけを止める。
  if (!error.response) {
    return;
  }

  // 現在すでにエラーページにいる場合は何もしない
  if (window.location.pathname === '/error') {
    return;
  }

  // エラーページへのリダイレクト
  window.dispatchEvent(new CustomEvent('system-error', { detail: { error } }));
};

// リクエストインターセプター（トークン付与）
axios.interceptors.request.use(
  config => {
    const activeSession = SessionManager.getActiveSession();
    if (activeSession && activeSession.token) {
      config.headers.Authorization = `Bearer ${activeSession.token}`;
    }
    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

// リトライカウンター
const retryMap = new Map();

// レスポンスインターセプター
axios.interceptors.response.use(
  response => {
    // リクエストが成功した場合、そのURLのリトライカウンターをリセット
    if (response.config.url) {
      retryMap.delete(response.config.url);
    }
    return response;
  },
  error => {
    // エラーページにアクセス中なら、エラーハンドリングをスキップ
    if (window.location.pathname === '/error') {
      return Promise.reject(error);
    }
    
    // ネットワークエラー（レスポンスなし）
    if (!error.response) {

      // ログAPIへのエラーの場合は無視する
      if (error.config?.url?.includes('/api/logs')) {
        return Promise.reject(error);
      }

      // 画面遷移やアンマウントによるリクエスト中断は「ネットワーク障害」ではない。
      // これを弾かないと、正常な操作のたびに到達性プローブ（/healthz + DB 疎通付きの
      // /api/health）が走ってしまう。
      if (axios.isCancel(error) || error.code === 'ERR_CANCELED') {
        return Promise.reject(error);
      }

      // サーバ到達性の確認を促す（useServerReachability が拾う）。
      // リトライのたびに発火するが、受け手側が最短間隔でスロットルするため
      // 実際のプローブは間引かれる。
      window.dispatchEvent(new CustomEvent(NETWORK_ERROR_EVENT));

      // ネットワークエラーの場合は、リクエストを再試行する
      const originalRequest = error.config;
      
      if (!originalRequest) {
        return Promise.reject(error);
      }
      
      // 再試行回数をカウント
      const url = originalRequest.url;
      const currentRetryCount = retryMap.get(url) || 0;
      
      // 最大3回まで再試行
      if (currentRetryCount < 3 && (error.message === 'Network Error' || error.code === 'ERR_NETWORK_CHANGED')) {
        retryMap.set(url, currentRetryCount + 1);
        
        // 再試行の間隔を徐々に増やす
        return new Promise(resolve => {
          setTimeout(() => {
            resolve(axios(originalRequest));
          }, 1000 * (currentRetryCount + 1)); 
        });
      }
      
      // エラー画面へ遷移（再試行が上限に達した場合）
      if (currentRetryCount >= 3) {
        navigateToErrorPage(error);
      }
      
      return Promise.reject(error);
    }

    // レスポンスエラー


    // ログアウトAPIなど、特定のリクエストでは401/403エラーを無視するための特別なヘッダーをチェック
    const skipAuthError = error.config?.headers?.['X-Skip-Auth-Error'] === 'true';
    // システムエラーへのリダイレクトをスキップする設定
    const skipErrorRedirect = error.config?.headers?.['X-Skip-Error-Redirect'] === 'true';

    // 401エラーの処理（認証切れ・トークン無効。X-Skip-Auth-Errorが設定されている場合は無視）
    // 403は「ログインは有効だが対象への権限がない」ケース（非公開動画への直リンク等）であり、
    // 認証失効とは別物のためセッションを破棄してログアウトさせてはいけない。
    // 接続元ゲート（requireInternalNetwork）の 403 INTERNAL_NETWORK_ONLY もこれに含まれる。
    if (error.response.status === 401 && !skipAuthError) {
      const activeSession = SessionManager.getActiveSession();
      if (activeSession) {
        // セッションを削除
        SessionManager.removeSession(activeSession.userId);

        // 独自のイベント発火（クライアント側でのリダイレクト処理用）
        redirectOnUnauthorized();
      }
    }
    // 5XXエラーや重大なエラーの場合はエラーページに遷移
    else if (!skipErrorRedirect && (
      error.response.status >= 500 || 
      error.response.data?.status === 'error'
    )) {
      navigateToErrorPage(error);
    }

    return Promise.reject(error);
  }
);

export default axios;
